import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';

// IMPORTS DES COMPOSANTS MODULAIRES
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000/api/';

interface Message {
    role: 'user' | 'assistant';
    content: string;
    created_at?: string;
}

interface Conversation {
    id: number;
    title: string;
    message_count: number;
    created_at: string;
    updated_at: string;
    last_message_preview?: string;
}

const FidexIA: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // État pour contrôler l'ouverture/fermeture du panneau de notifications
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);

    // États pour le chat
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'assistant',
            content: 'Bonjour ! Je suis votre assistant RH intelligent Fidex IA. Comment puis-je vous aider aujourd\'hui ?'
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    // États pour la gestion des conversations
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [showConversationList, setShowConversationList] = useState(false);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);

    // Protection de la route
    if (!user) {
        navigate('/login', { replace: true});
        return null;
    }

    // Charger les conversations au montage
    useEffect(() => {
        loadConversations();
    }, []);

    // Fonction pour charger la liste des conversations
    const loadConversations = async () => {
        setIsLoadingConversations(true);
        try {
            const token = localStorage.getItem('access');
            const response = await fetch(`${API_URL}ai/conversations/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setConversations(data.results || data || []);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des conversations:', error);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    // Fonction pour charger une conversation spécifique
    const loadConversation = async (conversationId: number) => {
        try {
            const token = localStorage.getItem('access');
            const response = await fetch(`${API_URL}ai/conversations/${conversationId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Charger les messages de la conversation
                const formattedMessages: Message[] = data.messages.map((msg: any) => ({
                    role: msg.role,
                    content: msg.content,
                    created_at: msg.created_at
                }));

                setMessages(formattedMessages.length > 0 ? formattedMessages : [
                    {
                        role: 'assistant',
                        content: 'Bonjour ! Je suis votre assistant RH intelligent Fidex IA. Comment puis-je vous aider aujourd\'hui ?'
                    }
                ]);
                setCurrentConversationId(conversationId);
                setShowConversationList(false);
            }
        } catch (error) {
            console.error('Erreur lors du chargement de la conversation:', error);
        }
    };

    // Fonction pour créer une nouvelle conversation
    const startNewConversation = () => {
        setMessages([
            {
                role: 'assistant',
                content: 'Bonjour ! Je suis votre assistant RH intelligent Fidex IA. Comment puis-je vous aider aujourd\'hui ?'
            }
        ]);
        setCurrentConversationId(null);
        setShowConversationList(false);
    };

    // Fonction pour supprimer une conversation
    const deleteConversation = async (conversationId: number, event: React.MouseEvent) => {
        event.stopPropagation();

        if (!confirm('Êtes-vous sûr de vouloir supprimer cette conversation ?')) {
            return;
        }

        try {
            const token = localStorage.getItem('access');
            const response = await fetch(`${API_URL}ai/conversations/${conversationId}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                // Recharger la liste des conversations
                loadConversations();

                // Si c'était la conversation active, démarrer une nouvelle
                if (currentConversationId === conversationId) {
                    startNewConversation();
                }
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la conversation:', error);
        }
    };

    // Fonction pour envoyer un message
    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        // Ajouter le message utilisateur
        const userMessage: Message = { role: 'user', content: inputMessage };
        setMessages(prev => [...prev, userMessage]);
        const currentMessage = inputMessage;
        setInputMessage('');
        setIsTyping(true);

        try {
            // Appel à l'API backend Fidex IA
            const token = localStorage.getItem('access');
            const response = await fetch(`${API_URL}ai/chat/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: currentMessage,
                    conversation_id: currentConversationId,
                    include_context: true
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                
                // Construire un message d'erreur détaillé
                let errorMessage = errorData?.error || errorData?.detail || `Erreur HTTP ${response.status}`;
                
                // Ajouter l'aide si disponible
                if (errorData?.help) {
                    errorMessage += `\n\n💡 ${errorData.help}`;
                }
                
                // Ajouter les détails si disponibles
                if (errorData?.details && errorData.details !== errorMessage) {
                    errorMessage += `\n\nDétails techniques: ${errorData.details}`;
                }
                
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Mettre à jour l'ID de conversation si c'est une nouvelle conversation
            if (!currentConversationId && data.conversation_id) {
                setCurrentConversationId(data.conversation_id);
                // Recharger la liste des conversations
                loadConversations();
            }

            const assistantMessage: Message = {
                role: 'assistant',
                content: data.response || 'Désolé, je n\'ai pas pu générer de réponse.'
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Erreur lors de l\'appel à l\'API:', error);

            // Message d'erreur informatif pour l'utilisateur
            let errorContent = '⚠️ Une erreur s\'est produite lors de la communication avec l\'assistant IA.';
            
            if (error instanceof Error) {
                // Afficher le message d'erreur complet (qui contient déjà error, help et details)
                errorContent = `⚠️ ${error.message}`;
            }
            
            const errorMessage: Message = {
                role: 'assistant',
                content: errorContent
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    // Questions rapides suggérées
    const quickQuestions = [
        'Combien d\'employés avons-nous ?',
        'Quels contrats expirent bientôt ?',
        'Statistiques du mois',
        'Prédiction de turnover',
    ];

    return (
        <div className={`flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>

            {/* -------------------- Sidebar (Composant Modulaire) -------------------- */}
            <Sidebar />

            {/* -------------------- Contenu Principal -------------------- */}
            <main className="flex-1 p-6 overflow-y-auto">

                {/* Navbar Supérieure (Composant Modulaire) */}
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />

                {/* En-tête de la page avec couleurs Fidex */}
                <div className="mb-8">
                    <div className="flex items-center justify-between gap-4 mb-2">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800">Fidex IA - Assistant RH Intelligent</h1>
                            <p className="text-slate-600">Votre assistant IA pour optimiser la gestion des ressources humaines</p>
                        </div>

                        {/* Boutons d'action */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowConversationList(!showConversationList)}
                                className="btn btn-outline border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913] hover:text-white"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                                </svg>
                                Historique ({conversations.length})
                            </button>
                            <button
                                onClick={startNewConversation}
                                className="btn bg-gradient-to-r from-[#FDB913] to-[#E5A511] hover:from-[#E5A511] hover:to-[#FDB913] text-white border-none"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Nouvelle conversation
                            </button>
                        </div>
                    </div>
                </div>

                {/* Grille principale */}
                <div className="grid grid-cols-1 gap-6">

                    {/* Chat Principal - Colonne unique élargie */}
                    <div>
                        {/* Liste des conversations (si affichée) */}
                        {showConversationList && (
                            <div className="mb-6 bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
                                <h3 className="text-lg font-bold text-slate-800 mb-4">Mes conversations</h3>
                                {isLoadingConversations ? (
                                    <p className="text-slate-600">Chargement...</p>
                                ) : conversations.length === 0 ? (
                                    <p className="text-slate-600">Aucune conversation sauvegardée</p>
                                ) : (
                                    <div className="space-y-2">
                                        {conversations.map(conv => (
                                            <div
                                                key={conv.id}
                                                onClick={() => loadConversation(conv.id)}
                                                className={`p-4 border rounded-xl cursor-pointer transition-all hover:border-[#FDB913] hover:shadow-md ${
                                                    currentConversationId === conv.id ? 'border-[#FDB913] bg-[#FDB913]/5' : 'border-slate-200'
                                                }`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-slate-800">{conv.title}</h4>
                                                        <p className="text-sm text-slate-600 mt-1">{conv.last_message_preview}</p>
                                                        <p className="text-xs text-slate-500 mt-2">
                                                            {conv.message_count} messages • {new Date(conv.updated_at).toLocaleDateString('fr-FR')}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={(e) => deleteConversation(conv.id, e)}
                                                        className="btn btn-ghost btn-sm text-red-500 hover:bg-red-50"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Zone de chat */}
                        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden h-[calc(100vh-250px)] flex flex-col">

                            {/* En-tête du chat */}
                            <div className="bg-gradient-to-r from-[#FDB913] to-[#E5A511] p-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-white">
                                            {currentConversationId ? 'Conversation en cours' : 'Nouvelle conversation'}
                                        </h2>
                                    </div>
                                </div>
                            </div>

                            {/* Zone de messages */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
                                {messages.map((message, index) => (
                                    <div
                                        key={index}
                                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`flex gap-3 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                            {/* Avatar */}
                                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                                message.role === 'user'
                                                    ? 'bg-slate-700'
                                                    : 'bg-gradient-to-br from-[#FDB913] to-[#E5A511]'
                                            }`}>
                                                {message.role === 'user' ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Bulle de message */}
                                            <div className={`rounded-2xl px-4 py-3 ${
                                                message.role === 'user'
                                                    ? 'bg-slate-700 text-white'
                                                    : 'bg-white border-2 border-[#FDB913]/20 text-slate-800'
                                            }`}>
                                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Indicateur de saisie */}
                                {isTyping && (
                                    <div className="flex justify-start">
                                        <div className="flex gap-3 max-w-[80%]">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#FDB913] to-[#E5A511] flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                </svg>
                                            </div>
                                            <div className="bg-white border-2 border-[#FDB913]/20 rounded-2xl px-4 py-3">
                                                <div className="flex gap-1">
                                                    <span className="w-2 h-2 bg-[#FDB913] rounded-full animate-bounce"></span>
                                                    <span className="w-2 h-2 bg-[#FDB913] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                                                    <span className="w-2 h-2 bg-[#FDB913] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Zone de saisie avec questions rapides */}
                            <div className="p-4 bg-white border-t border-slate-200">
                                {/* Questions rapides en forme de pillule */}
                                <div className="mb-4 flex flex-wrap gap-2">
                                    {quickQuestions.map((question, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setInputMessage(question)}
                                            className="px-4 py-2 bg-slate-100 hover:bg-[#FDB913] border border-slate-300 hover:border-[#FDB913] rounded-full transition-all duration-200 text-sm text-slate-700 hover:text-white font-medium"
                                        >
                                            {question}
                                        </button>
                                    ))}
                                </div>

                                {/* Champ de saisie et bouton d'envoi */}
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        placeholder="Posez votre question RH ici..."
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-[#FDB913] focus:ring-2 focus:ring-[#FDB913]/20 outline-none transition-all"
                                        disabled={isTyping}
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={isTyping || !inputMessage.trim()}
                                        className="btn bg-gradient-to-r from-[#FDB913] to-[#E5A511] hover:from-[#E5A511] hover:to-[#FDB913] text-white border-none shadow-lg px-6 disabled:opacity-50"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        Envoyer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* -------------------- Panneau de Notifications (Composant Modulaire) -------------------- */}
            <NotificationPanel
                isOpen={isNotifPanelOpen}
                onClose={() => setIsNotifPanelOpen(false)}
            />
        </div>
    );
};

export default FidexIA;